import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TBusinessFormData, TFormType } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider from 'src/components/hook-form';
import { IBusiness, useStores } from 'src/models';
import { ISubContentInfos } from 'src/models/management/business/SubContent';
import { businessApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import businessFormValidationSchema from 'src/static/yup/businessFormValidationSchema';
import { getBusinessFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import removeNullUndefined from 'src/utils/removeNullUndefined';

import { processImagePaths, processSubContentInfos } from '../utils/processImagePath';

import { Common } from './dialog-section/common/Common';
import { Contents } from './dialog-section/contents/Contents';
import { SubContentManager } from './dialog-section/sub-content/SubContentManager';

interface IBusinessFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IBusiness;
}

export const BusinessFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IBusinessFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const isCreate = formType === FormType.CREATE;

    const methods = useForm<TBusinessFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      resolver: yupResolver(businessFormValidationSchema),
      defaultValues: getBusinessFormDefaultValues(selectedData),
    });
    // 서브컨텐츠 필드 리스트
    const [subContentFields, setSubContentFields] = useState<ISubContentInfos[]>(
      selectedData?.subContentInfos ?? [],
    );

    // 등록 / 수정 함수
    const onValid = async (data: TBusinessFormData) => {
      // 썸네일 및 상세 이미지 처리
      const thumbnail = processImagePaths(
        [data.thumbPath],
        selectedData?.thumbPath ? [selectedData?.thumbPath] : [],
      );
      const detail = processImagePaths(data.detailPath || [], selectedData?.detailPath || []);
      console.log(methods.formState.errors);

      // 서브 컨텐츠 처리
      const subContentInfos = processSubContentInfos(
        data.subContentInfos || [],
        selectedData?.subContentInfos || [],
      );

      // formData
      const formData: TBusinessFormData = removeNullUndefined({
        ...data,
        thumbnailFiles: thumbnail.uploadFiles,
        thumbPath: thumbnail.existingPaths,
        deleteThumbnailPaths: thumbnail.deletePaths,
        detailFiles: detail.uploadFiles,
        detailPath: detail.existingPaths,
        deleteDetailPaths: detail.deletePaths,
        subContentInfos: subContentInfos.map((content) => ({
          title: content.title,
          descript: content.descript,
          desktopFiles: content.desktopFiles,
          desktopImagePath: content.desktopImagePath || [],
          deleteDesktopPaths: content.deleteDesktopPaths,
          mobileFiles: content.mobileFiles,
          mobileImagePath: content.mobileImagePath || [],
          deleteMobilePaths: content.deleteMobilePaths,
        })),
      });

      // 등록/수정 API 호출
      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate
        ? await businessApi.post(formData)
        : await businessApi.put(selectedData?.businessSid ?? 0, formData);

      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        businessApi
          .remove(selectedData?.businessSid ?? 0)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '사업 삭제',
        content: '해당 사업을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          {/* 기본 정보 + 메인 화면 business의 썸네일 */}
          <Common formType={formType} />
          {/* 컨텐츠: business 카테고리에 노출되는 상세 컨텐츠 */}
          <Contents />
          {/* 서브 컨텐츠: 컨텐츠 외 더 추가하고 싶을 경우 사용 */}
          <SubContentManager
            subContentFields={subContentFields}
            setSubContentFields={setSubContentFields}
            formType={formType}
          />
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {formType === FormType.CREATE ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
